/**
 * OFashion Wishlist V2.1
 *
 * Variant-aware wishlist stored in localStorage, optionally synced to a
 * customer metafield through a Shopify App Proxy (/apps/wishlist).
 *
 * - Storage key is versioned; migrate() handles older formats.
 * - One entry per product handle; saving from PDP replaces the card entry.
 * - All clicks handled via event delegation on document (works in quick
 *   shop modals, theme editor section reloads and AJAX-rendered cards).
 * - Custom events: wishlist:added / wishlist:removed / wishlist:updated.
 * - Cross-tab sync via the window "storage" event (receiving tab never pushes).
 * - Account sync (logged in): metafield document { rev, updatedAt, items } is
 *   the source of truth; localStorage is a cache. Serialized push queue with
 *   baseRev optimistic concurrency — 409 responses are merged client-side
 *   (except clearPending, which force-pushes empty).
 * - Add-to-cart from drawer/page; Add all to cart with run lifecycle + Clear.
 * - Empty state: intent copy + recently viewed recovery grid.
 * - Revenue-ready analytics via dataLayer (value via toMoneyMajor only).
 * - Debug: set window.WISHLIST_DEBUG = true (alias: window.__wishlistDebug).
 */
(function() {
  'use strict';

  window.theme = window.theme || {};
  if (theme.Wishlist) {
    return;
  }

  var STORAGE_KEY = 'ofashion:wishlist:v1';
  var META_KEY = 'ofashion:wishlist:meta:v1';
  var MERGED_KEY_PREFIX = 'ofashion:wishlist:merged:';
  var ATC_SESSION_KEY = 'ofashion:wishlist:atc:v1';
  var PURCHASE_FIRED_KEY = 'ofashion:wishlist:purchase_fired:v1';
  var LEGACY_KEYS = ['ofashion:wishlist'];
  var DRAWER_LIMIT = 20;
  var PAGE_BATCH = 24;
  var MAX_ITEMS = 100;
  var FETCH_TIMEOUT_MS = 10000;
  var BULK_ATC_DELAY_MS = 75;
  var ATC_SESSION_CAP = 50;
  var RECENTLY_VIEWED_LIMIT = 6;
  var PUSH_DEBOUNCE_MS = 1000;
  var RETRY_DELAYS_MS = [5000, 30000];
  var DRAWER_CLOSE_DELAY_MS = 350;
  var MAX_CONSECUTIVE_CONFLICTS = 3;

  var strings = (theme.strings && theme.strings.wishlist) || {};
  var routes = theme.routes || {};
  var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var wishlistUrl = routes.wishlist || '/pages/wishlist';
  var proxyUrl = routes.wishlistProxy || '/apps/wishlist';

  // In-memory copy used when localStorage is unavailable (private mode, quota).
  var memoryList = null;
  var productCache = new Map();
  var pageRenderedCount = 0;

  // V2.1 run / clear / toast state
  var atcInFlight = false;
  var bulkInFlight = false;
  var clearPending = false;
  var runState = 'idle';
  var currentRun = null;
  var pendingRender = false;
  var pendingAdoptList = null;
  var toastState = 'idle';
  var toastTimer = null;
  var toastHideTimer = null;
  var sessionCurrency = null;
  var firedAddAllRuns = {};

  function log() {
    if (window.WISHLIST_DEBUG === true || window.__wishlistDebug === true) {
      var args = ['[wishlist]'].concat(Array.prototype.slice.call(arguments));
      console.log.apply(console, args);
    }
  }

  function t(key, fallback, replacements) {
    var value = strings[key] || fallback;
    if (replacements) {
      Object.keys(replacements).forEach(function(name) {
        value = value.replace('[' + name + ']', replacements[name]);
      });
    }
    return value;
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatMoney(cents) {
    if (theme.Currency && theme.Currency.formatMoney) {
      return theme.Currency.formatMoney(cents, theme.settings && theme.settings.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  // Sole analytics money conversion — never inline ÷100 for event values.
  function toMoneyMajor(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) {
      return null;
    }
    return Number((cents / 100).toFixed(2));
  }

  function getSessionCurrency() {
    if (sessionCurrency) {
      return sessionCurrency;
    }
    var code = null;
    if (window.Shopify && Shopify.currency && Shopify.currency.active) {
      code = Shopify.currency.active;
    } else if (theme.settings && theme.settings.currency) {
      code = theme.settings.currency;
    } else if (theme.shop && theme.shop.currency) {
      code = theme.shop.currency;
    }
    if (code) {
      sessionCurrency = code;
    }
    return sessionCurrency;
  }

  function delay(ms) {
    return new Promise(function(resolve) {
      setTimeout(resolve, ms);
    });
  }

  // fetch with a hard timeout via AbortController. Used for every request.
  function fetchWithTimeout(url, options) {
    var controller = new AbortController();
    var timer = setTimeout(function() {
      controller.abort();
    }, FETCH_TIMEOUT_MS);
    var opts = Object.assign({}, options || {}, { signal: controller.signal });
    return fetch(url, opts).finally(function() {
      clearTimeout(timer);
    });
  }

  function safeGetItem(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (err) {
      return null;
    }
  }

  function safeSetItem(key, value) {
    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch (err) {
      return false;
    }
  }

  function safeSessionGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (err) {
      return null;
    }
  }

  function safeSessionSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
      return true;
    } catch (err) {
      return false;
    }
  }

  function safeSessionRemove(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (err) {
      // ignore
    }
  }

  /*============================================================
    Storage
  ============================================================*/

  function isValidItem(item) {
    return item && typeof item === 'object' && typeof item.handle === 'string' && item.handle.length > 0;
  }

  function sanitizeList(value) {
    if (!Array.isArray(value)) {
      return null;
    }
    return value.filter(isValidItem).map(function(item) {
      return {
        handle: item.handle,
        variantId: typeof item.variantId === 'number' ? item.variantId : null,
        addedAt: typeof item.addedAt === 'number' ? item.addedAt : Date.now()
      };
    });
  }

  function readList() {
    if (memoryList !== null) {
      return memoryList.slice();
    }
    var raw = null;
    try {
      raw = window.localStorage.getItem(STORAGE_KEY);
    } catch (err) {
      log('localStorage unavailable, using in-memory list', err);
      memoryList = [];
      return [];
    }
    if (raw === null) {
      return [];
    }
    try {
      var parsed = sanitizeList(JSON.parse(raw));
      if (parsed === null) {
        throw new Error('not an array');
      }
      return parsed;
    } catch (err) {
      log('corrupt storage, resetting', err);
      writeList([]);
      return [];
    }
  }

  function writeList(list) {
    if (memoryList !== null) {
      memoryList = list.slice();
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (err) {
      log('write failed, switching to in-memory list', err);
      memoryList = list.slice();
    }
  }

  // One-off, idempotent migration of older storage formats. Never throws.
  function migrate() {
    try {
      if (window.localStorage.getItem(STORAGE_KEY) !== null) {
        return;
      }
      LEGACY_KEYS.forEach(function(key) {
        var raw = window.localStorage.getItem(key);
        if (raw === null) {
          return;
        }
        try {
          var parsed = JSON.parse(raw);
          var migrated = null;
          if (Array.isArray(parsed) && parsed.every(function(v) { return typeof v === 'string'; })) {
            // Legacy format: plain array of handles.
            migrated = parsed.map(function(handle) {
              return { handle: handle, variantId: null, addedAt: Date.now() };
            });
          } else {
            migrated = sanitizeList(parsed);
          }
          if (migrated && migrated.length && window.localStorage.getItem(STORAGE_KEY) === null) {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
            log('migrated', key, '->', STORAGE_KEY, migrated.length + ' items');
          }
        } catch (err) {
          log('ignoring invalid legacy data on', key, err);
        }
        window.localStorage.removeItem(key);
      });
    } catch (err) {
      log('migration skipped', err);
    }
  }

  /*============================================================
    List operations (dedupe rule: one entry per handle)
  ============================================================*/

  function has(handle) {
    return readList().some(function(item) {
      return item.handle === handle;
    });
  }

  function add(item, source) {
    var list = readList().filter(function(existing) {
      return existing.handle !== item.handle;
    });
    var entry = {
      handle: item.handle,
      variantId: typeof item.variantId === 'number' ? item.variantId : null,
      addedAt: Date.now()
    };
    list.push(entry);

    // Hard cap: trim oldest so the metafield can never overflow.
    if (list.length > MAX_ITEMS) {
      list.sort(function(a, b) { return (a.addedAt || 0) - (b.addedAt || 0); });
      var trimmed = list.length - MAX_ITEMS;
      list = list.slice(trimmed);
      showToast(t('list_full', 'Önskelistan är full — den äldsta produkten togs bort'));
      log('trimmed', trimmed, 'oldest item(s), cap', MAX_ITEMS);
    }

    writeList(list);
    log('added', entry);
    dispatch('wishlist:added', entry, list, { source: source || 'api' });
    dispatch('wishlist:updated', entry, list, { source: source || 'api' });
    sync.schedulePush();
    return entry;
  }

  function remove(handle, source) {
    var list = readList();
    var removed = list.filter(function(item) {
      return item.handle === handle;
    })[0] || { handle: handle, variantId: null, addedAt: null };
    list = list.filter(function(item) {
      return item.handle !== handle;
    });
    writeList(list);
    log('removed', removed);
    dispatch('wishlist:removed', removed, list, { source: source || 'api' });
    dispatch('wishlist:updated', removed, list, { source: source || 'api' });
    sync.schedulePush();
    return removed;
  }

  function toggle(item, source) {
    if (has(item.handle)) {
      remove(item.handle, source);
      return false;
    }
    add(item, source);
    return true;
  }

  // Merge two lists: union per handle, newest addedAt wins.
  // Used for both guest-to-login merge and 409 conflict resolution.
  function mergeLists(a, b) {
    var byHandle = {};
    a.concat(b).forEach(function(item) {
      var existing = byHandle[item.handle];
      if (!existing || (item.addedAt || 0) > (existing.addedAt || 0)) {
        byHandle[item.handle] = item;
      }
    });
    var merged = Object.keys(byHandle).map(function(handle) {
      return byHandle[handle];
    });
    if (merged.length > MAX_ITEMS) {
      merged.sort(function(x, y) { return (y.addedAt || 0) - (x.addedAt || 0); });
      merged = merged.slice(0, MAX_ITEMS);
    }
    return merged;
  }

  // Replace local state from a remote/merged list WITHOUT dispatching
  // added/removed events or scheduling a push. UI is fully re-synced.
  function adoptList(list) {
    if (clearPending) {
      log('adopt skipped: clearPending');
      return;
    }
    if (bulkInFlight) {
      pendingAdoptList = list;
      log('adopt queued during bulk run');
      return;
    }
    writeList(list);
    syncBadge(list.length);
    syncAllButtons();
    updateBulkActions();
    if (isDrawerOpen()) {
      renderDrawer();
    }
    if (isOnWishlistPage()) {
      renderPage(true);
    }
  }

  function listsEqual(a, b) {
    if (a.length !== b.length) {
      return false;
    }
    var key = function(i) { return i.handle + ':' + i.variantId + ':' + i.addedAt; };
    var setA = a.map(key).sort().join('|');
    var setB = b.map(key).sort().join('|');
    return setA === setB;
  }

  /*============================================================
    Events + analytics
  ============================================================*/

  function dispatch(name, item, list, extra) {
    var detail = { item: item, list: list.slice(), count: list.length };
    if (extra) {
      Object.keys(extra).forEach(function(k) {
        detail[k] = extra[k];
      });
    }
    document.dispatchEvent(new CustomEvent(name, { detail: detail }));
  }

  document.addEventListener('wishlist:added', function(evt) {
    pushAnalytics('wishlist_add', evt.detail);
  });
  document.addEventListener('wishlist:removed', function(evt) {
    pushAnalytics('wishlist_remove', evt.detail);
  });

  function pushAnalytics(eventName, detail) {
    try {
      var payload = {
        event: eventName,
        item_handle: detail.item && detail.item.handle,
        variant_id: detail.item && detail.item.variantId,
        wishlist_count: detail.count,
        source: detail.source || 'api'
      };
      (window.dataLayer = window.dataLayer || []).push(payload);
      log('analytics', eventName, payload.item_handle, payload.source);
    } catch (err) {
      log('analytics failed', err);
    }
  }

  function pushDataLayer(payload) {
    try {
      (window.dataLayer = window.dataLayer || []).push(payload);
      log('analytics', payload.event, payload);
    } catch (err) {
      log('analytics failed', err);
    }
  }

  function attachMoneyFields(payload, value) {
    if (typeof value !== 'number' || isNaN(value)) {
      return payload;
    }
    var currency = getSessionCurrency();
    if (!currency) {
      return payload;
    }
    payload.value = Number(value.toFixed(2));
    payload.currency = currency;
    return payload;
  }

  function readAtcSession() {
    try {
      var raw = safeSessionGet(ATC_SESSION_KEY);
      if (!raw) {
        return [];
      }
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return [];
      }
      return parsed.filter(function(h) { return typeof h === 'string' && h.length; });
    } catch (err) {
      return [];
    }
  }

  function appendAtcSession(handle) {
    try {
      var list = readAtcSession();
      list.push(handle);
      if (list.length > ATC_SESSION_CAP) {
        list = list.slice(list.length - ATC_SESSION_CAP);
      }
      safeSessionSet(ATC_SESSION_KEY, JSON.stringify(list));
    } catch (err) {
      log('atc session write failed', err);
    }
  }

  function fireWishlistAddToCart(handle, variantId, context, valueMajor) {
    var payload = {
      event: 'wishlist_add_to_cart',
      item_handle: handle,
      variant_id: variantId,
      wishlist_count: readList().length,
      context: context || 'drawer'
    };
    attachMoneyFields(payload, valueMajor);
    pushDataLayer(payload);
    appendAtcSession(handle);
  }

  function fireWishlistAddAll(run, partial) {
    if (!run || !run.runId) {
      return;
    }
    if (firedAddAllRuns[run.runId]) {
      return;
    }
    firedAddAllRuns[run.runId] = true;
    var payload = {
      event: 'wishlist_add_all',
      added: run.added,
      skipped: run.skipped,
      failed: run.failed,
      wishlist_count: readList().length,
      partial: !!partial
    };
    if (typeof run.valueSum === 'number' && run.valueKnownCount > 0) {
      attachMoneyFields(payload, Number(run.valueSum.toFixed(2)));
      if (run.valueKnownCount < run.added) {
        payload.value_partial = true;
      }
    }
    pushDataLayer(payload);
  }

  function fireWishlistClear(clearedCount) {
    pushDataLayer({
      event: 'wishlist_clear',
      cleared_count: clearedCount,
      wishlist_count: 0
    });
  }

  function maybeFirePurchase() {
    try {
      if (safeSessionGet(PURCHASE_FIRED_KEY)) {
        return;
      }
      var handles = readAtcSession();
      if (!handles.length) {
        return;
      }

      var checkout = window.Shopify && Shopify.checkout;
      var thankYou = window.Shopify && Shopify.Checkout && Shopify.Checkout.page === 'thank_you';
      var pathMatch = /thank[_-]?you|\/orders\//i.test(window.location.pathname || '');
      if (!checkout && !thankYou && !pathMatch) {
        return;
      }

      var payload = {
        event: 'wishlist_purchase',
        item_handles: handles.slice()
      };
      if (checkout && checkout.order_id != null) {
        payload.order_id = checkout.order_id;
      } else if (checkout && checkout.order_number != null) {
        payload.order_id = checkout.order_number;
      }

      var cents = null;
      if (checkout && typeof checkout.total_price === 'number') {
        cents = checkout.total_price;
      } else if (checkout && typeof checkout.total_price === 'string' && checkout.total_price !== '') {
        cents = parseInt(checkout.total_price, 10);
      }
      var major = toMoneyMajor(cents);
      if (typeof major === 'number') {
        attachMoneyFields(payload, major);
        payload.value_source = 'checkout';
      }

      pushDataLayer(payload);
      safeSessionSet(PURCHASE_FIRED_KEY, '1');
      safeSessionRemove(ATC_SESSION_KEY);
    } catch (err) {
      log('purchase analytics failed', err);
    }
  }

  function valueFromCartLine(data) {
    if (!data) {
      return null;
    }
    if (typeof data.final_price === 'number') {
      return toMoneyMajor(data.final_price);
    }
    if (typeof data.price === 'number') {
      return toMoneyMajor(data.price);
    }
    return null;
  }

  function valueFromProductVariant(product, variantId) {
    if (!product || product.notFound) {
      return null;
    }
    var variant = findVariant(product, variantId);
    if (variant && typeof variant.price === 'number') {
      return toMoneyMajor(variant.price);
    }
    if (typeof product.price === 'number') {
      return toMoneyMajor(product.price);
    }
    return null;
  }

  /*============================================================
    Remote sync (logged-in customers, via App Proxy)

    Protocol: metafield holds { rev, updatedAt, items }. Client POSTs
    { baseRev, items }; worker writes rev = baseRev + 1 on match or
    replies 409 + current document on mismatch (client merges, re-pushes).
    While clearPending: always push items: []; on 409 adopt rev only and
    re-push empty (no merge). Serialized queue: max one in-flight push.
  ============================================================*/

  var sync = (function() {
    var state = {
      enabled: false,
      stopped: false,
      customerId: null,
      baseRev: 0,
      dirty: false,
      inFlight: false,
      retryCount: 0,
      conflictCount: 0,
      debounceTimer: null,
      retryTimer: null,
      pendingMergeFlag: false
    };

    function readMeta() {
      try {
        var parsed = JSON.parse(safeGetItem(META_KEY) || '{}');
        return { baseRev: typeof parsed.baseRev === 'number' && parsed.baseRev >= 0 ? parsed.baseRev : 0 };
      } catch (err) {
        return { baseRev: 0 };
      }
    }

    function writeMeta() {
      safeSetItem(META_KEY, JSON.stringify({ baseRev: state.baseRev }));
    }

    function parseRemoteDoc(raw) {
      try {
        if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
          var items = sanitizeList(raw.items) || [];
          var rev = typeof raw.rev === 'number' && raw.rev >= 0 ? Math.floor(raw.rev) : 0;
          return { rev: rev, items: items };
        }
        if (Array.isArray(raw)) {
          // Tolerate a manually created plain-array metafield.
          return { rev: 0, items: sanitizeList(raw) || [] };
        }
      } catch (err) {
        log('invalid remote doc', err);
      }
      return { rev: 0, items: [] };
    }

    function mergedFlagKey() {
      return MERGED_KEY_PREFIX + state.customerId;
    }

    // Bootstrap -> merge (first login per customer) -> adopt -> sync.
    function init() {
      state.customerId = theme.wishlistCustomerId || null;
      if (!state.customerId) {
        log('sync disabled: not logged in');
        return;
      }
      if (window.Shopify && window.Shopify.designMode) {
        log('sync disabled: theme editor preview');
        return;
      }
      state.enabled = true;

      var remote = parseRemoteDoc(theme.wishlistRemote);
      state.baseRev = remote.rev;
      writeMeta();

      var hasMerged = safeGetItem(mergedFlagKey()) !== null;
      var local = readList();

      if (!hasMerged) {
        var merged = mergeLists(local, remote.items);
        adoptList(merged);
        log('login merge:', local.length, 'local +', remote.items.length, 'remote ->', merged.length);
        if (listsEqual(merged, remote.items)) {
          safeSetItem(mergedFlagKey(), String(Date.now()));
        } else {
          state.pendingMergeFlag = true;
          schedulePush();
        }
      } else {
        // Adopt: remote is the source of truth, including an empty remote.
        if (!listsEqual(local, remote.items)) {
          adoptList(remote.items);
          log('adopted remote list,', remote.items.length, 'items');
        }
      }
    }

    function dropQueuedNonEmpty() {
      // Drop dirty / debounced non-empty payloads so clear cannot be undone.
      state.dirty = false;
      clearTimeout(state.debounceTimer);
      state.debounceTimer = null;
      clearTimeout(state.retryTimer);
      state.retryTimer = null;
      state.retryCount = 0;
    }

    function schedulePush() {
      if (!state.enabled || state.stopped) {
        return;
      }
      if (clearPending) {
        dropQueuedNonEmpty();
      }
      state.retryCount = 0;
      clearTimeout(state.retryTimer);
      clearTimeout(state.debounceTimer);
      state.debounceTimer = setTimeout(doPush, PUSH_DEBOUNCE_MS);
    }

    function doPush() {
      if (!state.enabled || state.stopped) {
        return;
      }
      if (state.inFlight) {
        state.dirty = true;
        return;
      }
      state.inFlight = true;
      var items = clearPending ? [] : readList();
      var payload = { baseRev: state.baseRev, items: items };
      log('push, baseRev', payload.baseRev, ',', payload.items.length, 'items', clearPending ? '(clearPending)' : '');

      fetchWithTimeout(proxyUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function(response) {
          if (response.status === 200) {
            return response.json().then(function(data) {
              onPushSuccess(data, items.length === 0);
            });
          }
          if (response.status === 409) {
            return response.json().then(function(doc) {
              onConflict(doc);
            });
          }
          if (response.status === 401 || response.status === 404) {
            state.stopped = true;
            log('sync stopped for session, HTTP', response.status);
            return;
          }
          throw new Error('HTTP ' + response.status);
        })
        .catch(function(err) {
          state.inFlight = false;
          log('push failed', err);
          scheduleRetry();
        });
    }

    function onPushSuccess(data, wasEmptyPush) {
      state.inFlight = false;
      state.retryCount = 0;
      state.conflictCount = 0;
      if (data && typeof data.rev === 'number') {
        state.baseRev = data.rev;
        writeMeta();
      }
      if (clearPending && (wasEmptyPush || readList().length === 0)) {
        clearPending = false;
        log('clearPending cleared after empty push');
      }
      if (state.pendingMergeFlag) {
        state.pendingMergeFlag = false;
        safeSetItem(mergedFlagKey(), String(Date.now()));
      }
      log('push ok, rev', state.baseRev);
      if (state.dirty) {
        state.dirty = false;
        doPush();
      }
    }

    function onConflict(doc) {
      state.inFlight = false;
      state.conflictCount++;
      var remote = parseRemoteDoc(doc);
      log('409 conflict, remote rev', remote.rev, ', attempt', state.conflictCount, clearPending ? '(clearPending)' : '');
      state.baseRev = remote.rev;
      writeMeta();

      if (clearPending) {
        // Authoritative clear: adopt rev only, do NOT merge/adopt items.
        if (state.conflictCount <= MAX_CONSECUTIVE_CONFLICTS) {
          doPush();
        } else {
          log('giving up clear re-push after repeated conflicts until next mutation');
          state.conflictCount = 0;
        }
        return;
      }

      var merged = mergeLists(readList(), remote.items);
      adoptList(merged);
      if (state.pendingMergeFlag) {
        // Merge resolution counts as completed login merge.
        state.pendingMergeFlag = false;
        safeSetItem(mergedFlagKey(), String(Date.now()));
      }
      if (state.conflictCount <= MAX_CONSECUTIVE_CONFLICTS) {
        doPush();
      } else {
        log('giving up after repeated conflicts until next mutation');
        state.conflictCount = 0;
      }
    }

    function scheduleRetry() {
      if (state.stopped || state.retryCount >= RETRY_DELAYS_MS.length) {
        // Give up until next mutation, page load or visibilitychange.
        state.dirty = true;
        return;
      }
      var delayMs = RETRY_DELAYS_MS[state.retryCount];
      state.retryCount++;
      log('retry in', delayMs, 'ms');
      clearTimeout(state.retryTimer);
      state.retryTimer = setTimeout(doPush, delayMs);
    }

    function flushBeacon() {
      if (!state.enabled || state.stopped || !navigator.sendBeacon) {
        return;
      }
      var pending = state.dirty || state.inFlight || state.debounceTimer !== null || clearPending;
      if (!pending) {
        return;
      }
      try {
        var items = clearPending ? [] : readList();
        var blob = new Blob(
          [JSON.stringify({ baseRev: state.baseRev, items: items })],
          { type: 'application/json' }
        );
        navigator.sendBeacon(proxyUrl, blob);
        log('beacon flush');
      } catch (err) {
        log('beacon failed', err);
      }
    }

    document.addEventListener('visibilitychange', function() {
      if (document.visibilityState === 'visible' && state.enabled && !state.stopped && state.dirty && !state.inFlight) {
        state.dirty = false;
        doPush();
      }
    });

    window.addEventListener('pagehide', flushBeacon);

    return {
      init: init,
      schedulePush: schedulePush,
      dropQueuedNonEmpty: dropQueuedNonEmpty,
      state: state
    };
  })();

  /*============================================================
    Product fetching (cached, in-flight deduped, locale-aware)
  ============================================================*/

  function fetchProduct(handle) {
    if (productCache.has(handle)) {
      return productCache.get(handle);
    }
    var promise = fetchWithTimeout(rootUrl + 'products/' + encodeURIComponent(handle) + '.js')
      .then(function(response) {
        if (response.status === 404 || response.status === 410) {
          return { notFound: true, handle: handle };
        }
        if (!response.ok) {
          throw new Error('HTTP ' + response.status);
        }
        return response.json();
      })
      .catch(function(err) {
        // Network/timeout errors are not cached so retry can re-fetch.
        productCache.delete(handle);
        throw err;
      });
    productCache.set(handle, promise);
    log('fetch', handle);
    return promise;
  }

  function productImage(product, width) {
    // /products/{handle}.js returns featured_image as a string URL, but
    // variant.featured_image (and some API variants) can be an object with .src.
    var raw = product.featured_image || (product.images && product.images[0]);
    var src = typeof raw === 'string' ? raw : (raw && raw.src);
    if (!src || typeof src !== 'string') {
      return null;
    }
    // Protocol-relative URLs (//cdn.shopify.com/...) often break in local
    // theme preview (http://127.0.0.1) — force https.
    if (src.indexOf('//') === 0) {
      src = 'https:' + src;
    }
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  // theme-recent stores lazysizes templates: ..._{width}x.jpg (and often //cdn...).
  // Resolve to a real <img src> the browser can fetch.
  function resolveRecentImageUrl(url, width) {
    if (!url || typeof url !== 'string') {
      return null;
    }
    var w = width || 540;
    if (url.indexOf('//') === 0) {
      url = 'https:' + url;
    }
    if (url.indexOf('{width}') !== -1) {
      return url.replace(/\{width\}/g, String(w));
    }
    return url;
  }

  function findVariant(product, variantId) {
    return (product.variants || []).filter(function(v) {
      return v.id === variantId;
    })[0] || null;
  }

  function purchasableVariants(product) {
    return (product.variants || []).filter(function(v) {
      return v.available;
    });
  }

  function canAddToCart(product) {
    return !product.notFound &&
      product.requires_selling_plan !== true &&
      purchasableVariants(product).length > 0;
  }

  function productUrl(product, variantId) {
    var url = rootUrl + 'products/' + product.handle;
    var variant = findVariant(product, variantId);
    if (variant && variant.available) {
      url += '?variant=' + variantId;
    }
    return url;
  }

  function productPrice(product, variantId) {
    var variant = findVariant(product, variantId);
    return variant ? variant.price : product.price;
  }

  function resolveVariantId(product, savedVariantId) {
    if (!product || product.notFound || !canAddToCart(product)) {
      return null;
    }
    var saved = findVariant(product, savedVariantId);
    if (saved && saved.available) {
      return saved.id;
    }
    var variants = purchasableVariants(product);
    return variants.length ? variants[0].id : null;
  }

  /*============================================================
    Toast state machine (single instance, aria-live)
    idle → loading|progress → success|error → idle
  ============================================================*/

  function getToastEl() {
    var el = document.querySelector('[data-wishlist-toast]');
    if (!el) {
      el = document.createElement('div');
      el.className = 'wishlist-toast';
      el.setAttribute('data-wishlist-toast', '');
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.hidden = true;
      document.body.appendChild(el);
    }
    return el;
  }

  function showToast(message, options) {
    options = options || {};
    var nextState = options.state || 'success';

    // Ignore programmatic single-ATC success toasts while bulk toast is active.
    if ((toastState === 'loading' || toastState === 'progress') &&
        nextState === 'success' && !options.bulk) {
      return;
    }

    toastState = nextState;
    var el = getToastEl();
    el.textContent = message;
    el.hidden = false;
    if (!el.classList.contains('wishlist-toast--visible')) {
      el.classList.add('wishlist-toast--visible');
    }

    clearTimeout(toastTimer);
    clearTimeout(toastHideTimer);

    // Hide timer ONLY on terminal success/error — never during loading/progress.
    if (nextState === 'success' || nextState === 'error') {
      toastTimer = setTimeout(function() {
        el.classList.remove('wishlist-toast--visible');
        toastHideTimer = setTimeout(function() {
          el.hidden = true;
          toastState = 'idle';
        }, 300);
      }, 2500);
    }
  }

  /*============================================================
    Add to cart (shared path + drawer/page single ATC + Add all)
  ============================================================*/

  function atcHtml(product, entry, context) {
    if (!canAddToCart(product)) {
      if (!product.notFound && product.requires_selling_plan !== true) {
        return '<button type="button" class="btn btn--secondary wishlist-atc__button" disabled>' +
          escapeHtml(t('sold_out', 'Slutsåld')) + '</button>';
      }
      return '';
    }
    var variants = purchasableVariants(product);
    var savedVariant = findVariant(product, entry.variantId);
    var selectedId = savedVariant && savedVariant.available ? savedVariant.id : variants[0].id;
    var html = '';

    if (variants.length > 1) {
      html += '<select class="wishlist-atc__select" data-wishlist-variant-select aria-label="' +
        escapeHtml(t('choose_variant', 'Välj variant')) + '">';
      variants.forEach(function(variant) {
        html += '<option value="' + variant.id + '"' + (variant.id === selectedId ? ' selected' : '') + '>' +
          escapeHtml(variant.title) + '</option>';
      });
      html += '</select>';
    }

    html += '<button type="button" class="btn wishlist-atc__button" data-wishlist-atc' +
      ' data-product-handle="' + escapeHtml(product.handle) + '"' +
      ' data-variant-id="' + selectedId + '"' +
      ' data-wishlist-context="' + context + '">' +
      '<span>' + escapeHtml(t('add_to_cart', 'Lägg i varukorgen')) + '</span>' +
      '</button>';

    return '<div class="wishlist-atc">' + html + '</div>';
  }

  // Shared by single ATC and bulk Add all to cart.
  function addVariantToCart(handle, variantId) {
    return fetchWithTimeout(routes.cartAdd || '/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: 'id=' + variantId + '&quantity=1&properties%5B_wishlist%5D=1'
    }).then(function(response) {
      return response.json().then(function(data) {
        return { status: response.status, data: data, handle: handle, variantId: variantId };
      }).catch(function() {
        return { status: response.status, data: {}, handle: handle, variantId: variantId };
      });
    });
  }

  function handleAtcClick(btn) {
    if (bulkInFlight || clearPending || atcInFlight || btn.disabled) {
      return;
    }
    var handle = btn.getAttribute('data-product-handle');
    var context = btn.getAttribute('data-wishlist-context') || 'drawer';
    var item = btn.closest('[data-wishlist-item]');
    var select = item && item.querySelector('[data-wishlist-variant-select]');
    var variantId = parseInt((select && select.value) || btn.getAttribute('data-variant-id'), 10);

    if (!handle || !variantId) {
      return;
    }

    // Lock synchronously at click time so double clicks (even within the
    // same task) can never produce two POSTs.
    atcInFlight = true;
    btn.classList.add('btn--loading');
    btn.disabled = true;

    // Click-time validation against the cached product (inventory race guard).
    fetchProduct(handle).then(function(product) {
      var variant = findVariant(product, variantId);
      if (!variant || !variant.available || !canAddToCart(product)) {
        log('atc blocked: variant unavailable at click time', handle, variantId);
        resetAtcButton(btn);
        showToast(t('cart_error', 'Produkten kunde inte läggas i varukorgen'));
        productCache.delete(handle);
        rerenderContext(context);
        return;
      }
      postAddToCart(btn, handle, variantId, context, product);
    }).catch(function() {
      resetAtcButton(btn);
      showToast(t('error', 'Något gick fel.'));
    });
  }

  function resetAtcButton(btn) {
    atcInFlight = false;
    btn.classList.remove('btn--loading');
    btn.disabled = false;
  }

  function postAddToCart(btn, handle, variantId, context, product) {
    addVariantToCart(handle, variantId)
      .then(function(result) {
        resetAtcButton(btn);

        if (result.status === 422 || (result.data && result.data.status === 422)) {
          log('atc 422', result.data);
          showToast((result.data && result.data.description) || t('cart_error', 'Produkten kunde inte läggas i varukorgen'));
          productCache.delete(handle);
          rerenderContext(context);
          return;
        }

        if (result.status < 200 || result.status >= 300) {
          log('atc failed status', result.status);
          showToast(t('error', 'Något gick fel.'));
          return;
        }

        log('atc ok', handle, variantId);
        var valueMajor = valueFromCartLine(result.data);
        if (valueMajor == null && product) {
          valueMajor = valueFromProductVariant(product, variantId);
        }
        fireWishlistAddToCart(handle, variantId, context, valueMajor);
        showToast(t('added_to_cart', 'Tillagd i varukorgen'));

        if (theme.settings && theme.settings.wishlistMoveToCart) {
          remove(handle);
        }

        if (!bulkInFlight) {
          openCartAfterAdd(result.data, context);
        }
      })
      .catch(function(err) {
        resetAtcButton(btn);
        log('atc failed', err);
        showToast(t('error', 'Något gick fel.'));
      });
  }

  function isCartDrawerOpen() {
    var drawer = document.getElementById('CartDrawer');
    return !!(drawer && drawer.classList.contains('drawer--is-open'));
  }

  function openCartAfterAdd(product, context) {
    if (bulkInFlight) {
      return;
    }
    var cartIsDrawer = !theme.settings || theme.settings.cartType === 'drawer';
    if (!cartIsDrawer) {
      window.location.href = routes.cartPage || '/cart';
      return;
    }
    if (isCartDrawerOpen()) {
      return;
    }
    var dispatchAdded = function() {
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        detail: { product: product, addToCartBtn: null }
      }));
    };
    // From the wishlist drawer: close it first, let the transition finish,
    // then open the cart drawer — never two overlapping drawers.
    if (context === 'drawer' && isDrawerOpen()) {
      closeWishlistDrawer();
      setTimeout(dispatchAdded, DRAWER_CLOSE_DELAY_MS);
    } else {
      dispatchAdded();
    }
  }

  function rerenderContext(context) {
    if (context === 'page') {
      renderPage(true);
    } else {
      renderDrawer();
    }
  }

  /*============================================================
    Add all to cart — formal run lifecycle
    idle → starting → running → finishing → completed → idle
  ============================================================*/

  function newRunId() {
    return String(Date.now()) + '-' + Math.random().toString(36).slice(2, 9);
  }

  function setWishlistLocked(locked) {
    document.querySelectorAll('#WishlistDrawer, [data-wishlist-grid], [data-wishlist-page-toolbar], [data-wishlist-bulk-actions]')
      .forEach(function(el) {
        el.classList.toggle('wishlist--locked', locked);
      });
    var pageRoot = document.querySelector('[data-wishlist-grid]');
    if (pageRoot && pageRoot.parentElement) {
      pageRoot.parentElement.classList.toggle('wishlist--locked', locked);
    }
  }

  function updateBulkActions() {
    ensureBulkContainers();
    var count = readList().length;
    var html = '';
    if (count >= 1) {
      html =
        '<button type="button" class="btn wishlist-bulk__add-all" data-wishlist-add-all' +
        (bulkInFlight || atcInFlight || clearPending ? ' disabled' : '') + '>' +
        escapeHtml(t('add_all', 'Add all to cart')) + '</button>' +
        '<button type="button" class="btn btn--secondary wishlist-bulk__clear" data-wishlist-clear' +
        (bulkInFlight || clearPending ? ' disabled' : '') + '>' +
        escapeHtml(t('clear', 'Clear wishlist')) + '</button>';
    }
    document.querySelectorAll('[data-wishlist-bulk-actions], [data-wishlist-page-toolbar]').forEach(function(el) {
      if (count < 1) {
        el.innerHTML = '';
        el.hidden = true;
      } else {
        el.hidden = false;
        el.innerHTML = html;
      }
    });
  }

  function ensureBulkContainers() {
    var drawerFooter = document.querySelector('#WishlistDrawer .drawer__footer');
    if (drawerFooter && !document.querySelector('[data-wishlist-bulk-actions]')) {
      var drawerActions = document.createElement('div');
      drawerActions.setAttribute('data-wishlist-bulk-actions', '');
      drawerActions.className = 'wishlist-bulk-actions';
      drawerActions.hidden = true;
      drawerFooter.insertBefore(drawerActions, drawerFooter.firstChild);
    }
    var pageGrid = document.querySelector('[data-wishlist-grid]');
    if (pageGrid && !document.querySelector('[data-wishlist-page-toolbar]')) {
      var toolbar = document.createElement('div');
      toolbar.setAttribute('data-wishlist-page-toolbar', '');
      toolbar.className = 'wishlist-page-toolbar';
      toolbar.hidden = true;
      pageGrid.parentNode.insertBefore(toolbar, pageGrid);
    }
  }

  function requestRender() {
    if (bulkInFlight) {
      pendingRender = true;
      return;
    }
    flushRender();
  }

  function flushRender() {
    pendingRender = false;
    syncBadge();
    syncAllButtons();
    updateBulkActions();
    if (isDrawerOpen()) {
      renderDrawer();
    }
    if (isOnWishlistPage()) {
      renderPage(true);
    }
  }

  function addAllToCart(context) {
    if (bulkInFlight || clearPending || atcInFlight) {
      return;
    }

    var snapshot = sortedList().map(function(item) {
      return { handle: item.handle, variantId: item.variantId, addedAt: item.addedAt };
    });
    if (!snapshot.length) {
      return;
    }

    // starting
    runState = 'starting';
    bulkInFlight = true;
    pendingRender = false;
    pendingAdoptList = null;
    currentRun = {
      runId: newRunId(),
      context: context || (isOnWishlistPage() ? 'page' : 'drawer'),
      snapshot: snapshot,
      added: 0,
      skipped: 0,
      failed: 0,
      valueSum: 0,
      valueKnownCount: 0,
      addedHandles: [],
      lastCartProduct: null,
      finishingStarted: false
    };
    setWishlistLocked(true);
    updateBulkActions();
    log('addAll starting', currentRun.runId, 'M=', snapshot.length);

    runState = 'running';
    var M = snapshot.length;
    if (M > 8) {
      showToast(t('adding_products', 'Adding products…'), { state: 'loading', bulk: true });
    }

    processBulkLine(0).catch(function(err) {
      log('addAll unexpected error', err);
      finishRun(false);
    });
  }

  function processBulkLine(index) {
    if (!currentRun || runState !== 'running') {
      return Promise.resolve();
    }
    var snapshot = currentRun.snapshot;
    if (index >= snapshot.length) {
      return finishRun(false);
    }

    var entry = snapshot[index];
    var N = index + 1;
    var M = snapshot.length;

    return processOneBulkEntry(entry)
      .then(function() {
        if (runState !== 'running') {
          return;
        }
        showToast(
          t('adding_progress', 'Adding [n]/[m]…', { n: N, m: M }),
          { state: 'progress', bulk: true }
        );
        return delay(BULK_ATC_DELAY_MS).then(function() {
          return processBulkLine(index + 1);
        });
      });
  }

  function processOneBulkEntry(entry) {
    return fetchProduct(entry.handle)
      .then(function(product) {
        var variantId = resolveVariantId(product, entry.variantId);
        if (!variantId) {
          currentRun.skipped++;
          return;
        }
        return attemptBulkAdd(entry.handle, variantId, product, false);
      })
      .catch(function(err) {
        log('bulk line fetch failed', entry.handle, err);
        // Fetch failure → evict + fresh fetch, then normal cart attempt (still gets one cart retry).
        productCache.delete(entry.handle);
        return fetchProduct(entry.handle)
          .then(function(product) {
            var variantId = resolveVariantId(product, null);
            if (!variantId) {
              currentRun.skipped++;
              return;
            }
            return attemptBulkAdd(entry.handle, variantId, product, false);
          })
          .catch(function(err2) {
            log('bulk line retry fetch failed', entry.handle, err2);
            currentRun.failed++;
          });
      });
  }

  function attemptBulkAdd(handle, variantId, product, isRetry) {
    return addVariantToCart(handle, variantId)
      .then(function(result) {
        var is422 = result.status === 422 || (result.data && result.data.status === 422);
        var ok = result.status >= 200 && result.status < 300 && !is422;

        if (ok) {
          recordBulkAdded(handle, variantId, result.data, product);
          return;
        }

        if (!isRetry) {
          // One retry: evict cache, fresh fetch, first available variant.
          productCache.delete(handle);
          return fetchProduct(handle)
            .then(function(fresh) {
              var retryId = resolveVariantId(fresh, null);
              if (!retryId) {
                currentRun.skipped++;
                return;
              }
              return attemptBulkAdd(handle, retryId, fresh, true);
            })
            .catch(function(err) {
              log('bulk retry fetch failed', handle, err);
              currentRun.failed++;
            });
        }

        // Retry still failed with a variantId → failed
        currentRun.failed++;
      })
      .catch(function(err) {
        log('bulk add failed', handle, err);
        if (!isRetry) {
          productCache.delete(handle);
          return fetchProduct(handle)
            .then(function(fresh) {
              var retryId = resolveVariantId(fresh, null);
              if (!retryId) {
                currentRun.skipped++;
                return;
              }
              return attemptBulkAdd(handle, retryId, fresh, true);
            })
            .catch(function(err2) {
              log('bulk retry after network failed', handle, err2);
              currentRun.failed++;
            });
        }
        currentRun.failed++;
      });
  }

  function recordBulkAdded(handle, variantId, cartData, product) {
    currentRun.added++;
    currentRun.addedHandles.push(handle);
    currentRun.lastCartProduct = cartData;

    var valueMajor = valueFromCartLine(cartData);
    if (valueMajor == null) {
      valueMajor = valueFromProductVariant(product, variantId);
    }
    // Per-line event BEFORE summary (ordering guarantee).
    fireWishlistAddToCart(handle, variantId, 'add_all', valueMajor);
    if (typeof valueMajor === 'number') {
      currentRun.valueSum += valueMajor;
      currentRun.valueKnownCount++;
    }
  }

  function finishRun(partial) {
    if (!currentRun) {
      runState = 'idle';
      bulkInFlight = false;
      return Promise.resolve();
    }
    if (currentRun.finishingStarted) {
      return Promise.resolve();
    }
    currentRun.finishingStarted = true;
    runState = 'finishing';
    log('addAll finishing', currentRun.runId, 'partial=', !!partial,
      'added=', currentRun.added, 'skipped=', currentRun.skipped, 'failed=', currentRun.failed);

    var run = currentRun;
    var context = run.context;

    // Move-to-cart removals only in finishing (while still locked / bulkInFlight
    // so renders stay queued for a single flush).
    if (theme.settings && theme.settings.wishlistMoveToCart && run.addedHandles.length) {
      run.addedHandles.forEach(function(handle) {
        // remove() schedules sync push; skip analytics noise by using write path
        var list = readList().filter(function(item) {
          return item.handle !== handle;
        });
        writeList(list);
      });
      sync.schedulePush();
      pendingRender = true;
    }

    // Apply queued sync adoption
    if (pendingAdoptList) {
      var adopted = pendingAdoptList;
      pendingAdoptList = null;
      if (!clearPending) {
        writeList(adopted);
      }
      pendingRender = true;
    }

    // Single unlock point (bulkInFlight was held true through finishing mutations
    // so renders stayed queued; unlock before the one flush).
    bulkInFlight = false;
    setWishlistLocked(false);

    // Flush queued renders (one full re-render)
    flushRender();

    // One wishlist_add_all per runId (normal or pagehide partial, never both)
    fireWishlistAddAll(run, partial);

    // Terminal toast summary
    var unavailable = run.skipped + run.failed;
    showToast(
      t('add_all_result', '[added] added · [skipped] unavailable', {
        added: run.added,
        skipped: unavailable
      }),
      { state: run.added > 0 || unavailable === 0 ? 'success' : 'error', bulk: true }
    );

    // Guarded cart open once if added ≥ 1 and cart drawer not already open
    if (run.added >= 1 && !partial) {
      if (!isCartDrawerOpen()) {
        openCartAfterAdd(run.lastCartProduct, context);
      }
    }

    runState = 'completed';
    currentRun = null;
    runState = 'idle';
    updateBulkActions();
    return Promise.resolve();
  }

  function clear() {
    if (bulkInFlight) {
      return false;
    }
    var list = readList();
    if (!list.length) {
      return false;
    }
    var confirmed = window.confirm(
      t('clear_confirm', 'Clear your entire wishlist?')
    );
    if (!confirmed) {
      return false;
    }

    var clearedCount = list.length;
    clearPending = true;
    sync.dropQueuedNonEmpty();
    writeList([]);
    dispatch('wishlist:updated', { handle: null, variantId: null, addedAt: null }, []);
    if (sync.state.enabled && !sync.state.stopped) {
      sync.schedulePush();
    } else {
      // No remote sync — clear is already authoritative locally.
      clearPending = false;
    }
    requestRender();
    showToast(t('cleared_toast', 'Wishlist cleared'));
    fireWishlistClear(clearedCount);
    log('cleared', clearedCount);
    return true;
  }

  /*============================================================
    UI sync: buttons + badge
  ============================================================*/

  function setButtonState(btn, active) {
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    var title = btn.getAttribute('data-product-title') || '';
    btn.setAttribute('aria-label', active
      ? t('remove_label', 'Ta bort [title] från önskelistan', { title: title })
      : t('add_label', 'Lägg till [title] i önskelistan', { title: title }));
    var label = btn.querySelector('[data-wishlist-label]');
    if (label) {
      label.textContent = active ? t('saved', 'Sparad') : t('save', 'Spara');
    }
  }

  // Targeted: only touch buttons for one product handle.
  function syncButtonsForHandle(handle, active) {
    document.querySelectorAll('[data-wishlist-button][data-product-handle="' + handle + '"]')
      .forEach(function(btn) {
        setButtonState(btn, active);
      });
  }

  // Full pass: init, shopify:section:load and cross-tab sync only.
  function syncAllButtons(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var saved = {};
    readList().forEach(function(item) {
      saved[item.handle] = true;
    });
    scope.querySelectorAll('[data-wishlist-button]').forEach(function(btn) {
      setButtonState(btn, !!saved[btn.getAttribute('data-product-handle')]);
    });
  }

  function syncBadge(count) {
    if (typeof count !== 'number') {
      count = readList().length;
    }
    document.querySelectorAll('[data-wishlist-count]').forEach(function(badge) {
      badge.textContent = count > 9 ? '9+' : String(count);
      badge.hidden = count === 0;
      badge.classList.remove('wishlist-link__bubble--pop');
      // Force reflow so the pop animation can replay.
      void badge.offsetWidth;
      if (count > 0) {
        badge.classList.add('wishlist-link__bubble--pop');
      }
    });
    document.querySelectorAll('[data-wishlist-title-count]').forEach(function(el) {
      el.textContent = count > 0 ? '(' + count + ')' : '';
    });
  }

  /*============================================================
    Rendering: shared item markup
  ============================================================*/

  function skeletonHtml(count) {
    var html = '';
    for (var i = 0; i < count; i++) {
      html += '<div class="wishlist-skeleton"><div class="wishlist-skeleton__image"></div><div class="wishlist-skeleton__lines"><div class="wishlist-skeleton__line"></div><div class="wishlist-skeleton__line wishlist-skeleton__line--short"></div></div></div>';
    }
    return html;
  }

  function removeButtonHtml(handle, title) {
    return '<button type="button" class="wishlist-item__remove" data-wishlist-remove data-product-handle="' + escapeHtml(handle) + '"' +
      ' aria-label="' + escapeHtml(t('remove_label', 'Ta bort [title] från önskelistan', { title: title })) + '">' +
      '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" viewBox="0 0 64 64"><path d="M19 17.61l27.12 27.13m0-27.12L19 44.74"/></svg>' +
      '</button>';
  }

  function heartIconSvg() {
    return '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-heart" viewBox="0 0 64 64"><path d="M32 54S6 38 6 22.5C6 13.9 12.9 8 20.5 8c4.7 0 9 2.3 11.5 6C34.5 10.3 38.8 8 43.5 8 51.1 8 58 13.9 58 22.5 58 38 32 54 32 54z"/></svg>';
  }

  function unavailableItemHtml(handle) {
    var title = handle.replace(/-/g, ' ');
    return '<div class="wishlist-item wishlist-item--unavailable" data-wishlist-item>' +
      '<div class="wishlist-item__image wishlist-item__image--placeholder"></div>' +
      '<div class="wishlist-item__meta">' +
      '<div class="wishlist-item__title">' + escapeHtml(title) + '</div>' +
      '<div class="wishlist-item__note">' + escapeHtml(t('unavailable', 'Inte längre tillgänglig')) + '</div>' +
      '</div>' +
      removeButtonHtml(handle, title) +
      '</div>';
  }

  function itemHtml(product, entry) {
    if (product.notFound) {
      return unavailableItemHtml(entry.handle);
    }
    var url = productUrl(product, entry.variantId);
    var image = productImage(product, 360);
    var price = productPrice(product, entry.variantId);
    return '<div class="wishlist-item" data-wishlist-item data-product-handle="' + escapeHtml(entry.handle) + '">' +
      '<a href="' + escapeHtml(url) + '" class="wishlist-item__image">' +
      (image ? '<img src="' + escapeHtml(image) + '" alt="' + escapeHtml(product.title) + '" loading="lazy">' : '') +
      '</a>' +
      '<div class="wishlist-item__meta">' +
      '<a href="' + escapeHtml(url) + '" class="wishlist-item__title">' + escapeHtml(product.title) + '</a>' +
      '<div class="wishlist-item__price">' + formatMoney(price) + '</div>' +
      atcHtml(product, entry, 'drawer') +
      '</div>' +
      removeButtonHtml(entry.handle, product.title) +
      '</div>';
  }

  function gridItemHtml(product, entry) {
    if (product.notFound) {
      return '<div class="grid__item medium-up--one-quarter small--one-half">' + unavailableItemHtml(entry.handle) + '</div>';
    }
    var url = productUrl(product, entry.variantId);
    var image = productImage(product, 540);
    var price = productPrice(product, entry.variantId);
    return '<div class="grid__item medium-up--one-quarter small--one-half wishlist-grid__item">' +
      '<div class="wishlist-card" data-wishlist-item data-product-handle="' + escapeHtml(entry.handle) + '">' +
      removeButtonHtml(entry.handle, product.title) +
      '<a href="' + escapeHtml(url) + '" class="wishlist-card__image">' +
      (image ? '<img src="' + escapeHtml(image) + '" alt="' + escapeHtml(product.title) + '" loading="lazy">' : '') +
      '</a>' +
      '<div class="wishlist-card__meta">' +
      '<a href="' + escapeHtml(url) + '" class="wishlist-card__title">' + escapeHtml(product.title) + '</a>' +
      '<div class="wishlist-card__price">' + formatMoney(price) + '</div>' +
      atcHtml(product, entry, 'page') +
      '</div>' +
      '</div>' +
      '</div>';
  }

  function getRecentlyViewedEntries() {
    var recent = theme.recentlyViewed && theme.recentlyViewed.recent;
    if (!recent || typeof recent !== 'object') {
      return [];
    }
    var wishlisted = {};
    readList().forEach(function(item) {
      wishlisted[item.handle] = true;
    });
    var seen = {};
    var entries = [];
    var keys = Object.keys(recent);
    // Impulse stores newest last (delete + re-add); reverse for newest-first.
    for (var i = keys.length - 1; i >= 0; i--) {
      var handle = keys[i];
      if (!handle || seen[handle] || wishlisted[handle]) {
        continue;
      }
      seen[handle] = true;
      var data = recent[handle] || {};
      var url = data.url;
      if (!url || typeof url !== 'string') {
        continue;
      }
      var title = handle.replace(/-/g, ' ');
      var image = resolveRecentImageUrl(data.featuredImage, 540);
      var info = theme.recentlyViewed.productInfo && theme.recentlyViewed.productInfo[handle];
      if (typeof info === 'string') {
        try {
          info = JSON.parse(info);
        } catch (e) {
          info = null;
        }
      }
      if (info && info.title) {
        title = info.title;
      }
      if (!image && info) {
        image = productImage(info, 540);
      }
      entries.push({
        handle: handle,
        url: url,
        title: title,
        image: image,
        aspectRatio: data.aspectRatio || null
      });
      if (entries.length >= RECENTLY_VIEWED_LIMIT) {
        break;
      }
    }
    return entries;
  }

  function recentCardHtml(entry) {
    var imageHtml = entry.image
      ? '<img src="' + escapeHtml(entry.image) + '" alt="' + escapeHtml(entry.title) + '" loading="lazy">'
      : '';
    return '<div class="wishlist-recent__card" data-wishlist-recent-item data-product-handle="' + escapeHtml(entry.handle) + '">' +
      '<a href="' + escapeHtml(entry.url) + '" class="wishlist-recent__image wishlist-item__image' +
      (entry.image ? '' : ' wishlist-item__image--placeholder') + '">' + imageHtml + '</a>' +
      '<div class="wishlist-recent__meta">' +
      '<a href="' + escapeHtml(entry.url) + '" class="wishlist-recent__product-title">' + escapeHtml(entry.title) + '</a>' +
      '<button type="button" class="wishlist-button wishlist-button--card" data-wishlist-button' +
      ' data-wishlist-source="recent_empty"' +
      ' data-product-handle="' + escapeHtml(entry.handle) + '"' +
      ' data-product-title="' + escapeHtml(entry.title) + '"' +
      ' aria-pressed="false"' +
      ' aria-label="' + escapeHtml(t('add_label', 'Lägg till [title] i önskelistan', { title: entry.title })) + '">' +
      heartIconSvg() +
      '</button>' +
      '</div></div>';
  }

  function emptyHtml(context) {
    var intent = t('empty_intent', 'You have no saved items — save your favorites to find them later');
    var cta = '<a href="' + escapeHtml(rootUrl + 'collections/all') + '" class="btn">' +
      escapeHtml(t('empty_cta', 'Shoppa nu')) + '</a>';

    if (window.Shopify && window.Shopify.designMode) {
      return '<div class="wishlist-empty">' +
        '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-heart wishlist-empty__icon" viewBox="0 0 64 64"><path d="M32 54S6 38 6 22.5C6 13.9 12.9 8 20.5 8c4.7 0 9 2.3 11.5 6C34.5 10.3 38.8 8 43.5 8 51.1 8 58 13.9 58 22.5 58 38 32 54 32 54z"/></svg>' +
        '<p class="wishlist-empty__text">' + escapeHtml(intent) + '</p>' +
        cta +
        '</div>';
    }

    var recent = getRecentlyViewedEntries();
    var recentHtml = '';
    if (recent.length) {
      recentHtml = '<div class="wishlist-recent">' +
        '<p class="wishlist-recent__heading">' + escapeHtml(t('empty_recent_title', 'Recently viewed')) + '</p>' +
        '<div class="wishlist-recent__grid">' +
        recent.map(recentCardHtml).join('') +
        '</div></div>';
    }

    return '<div class="wishlist-empty">' +
      '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-heart wishlist-empty__icon" viewBox="0 0 64 64"><path d="M32 54S6 38 6 22.5C6 13.9 12.9 8 20.5 8c4.7 0 9 2.3 11.5 6C34.5 10.3 38.8 8 43.5 8 51.1 8 58 13.9 58 22.5 58 38 32 54 32 54z"/></svg>' +
      '<p class="wishlist-empty__text">' + escapeHtml(intent) + '</p>' +
      cta +
      recentHtml +
      '</div>';
  }

  function errorHtml(context) {
    return '<div class="wishlist-error">' +
      '<p>' + escapeHtml(t('error', 'Något gick fel.')) + '</p>' +
      '<button type="button" class="btn btn--secondary" data-wishlist-retry data-wishlist-context="' + context + '">' +
      escapeHtml(t('retry', 'Försök igen')) +
      '</button>' +
      '</div>';
  }

  function sortedList() {
    return readList().sort(function(a, b) {
      return (b.addedAt || 0) - (a.addedAt || 0);
    });
  }

  function fetchEntries(entries) {
    // allSettled semantics: one broken product never blanks the whole view.
    return Promise.all(entries.map(function(entry) {
      return fetchProduct(entry.handle)
        .then(function(product) {
          return { entry: entry, product: product };
        })
        .catch(function(err) {
          log('fetch failed', entry.handle, err);
          return { entry: entry, failed: true };
        });
    }));
  }

  /*============================================================
    Drawer rendering (idempotent — always items, empty or error)
  ============================================================*/

  function renderDrawer() {
    if (bulkInFlight) {
      pendingRender = true;
      return;
    }
    var container = document.querySelector('[data-wishlist-drawer-items]');
    if (!container) {
      return;
    }
    var list = sortedList();
    log('renderDrawer', list.length + ' items');
    updateBulkActions();

    if (list.length === 0) {
      container.innerHTML = emptyHtml('drawer');
      syncAllButtons(container);
      return;
    }

    var visible = list.slice(0, DRAWER_LIMIT);
    container.innerHTML = skeletonHtml(Math.min(visible.length, 4));

    fetchEntries(visible).then(function(results) {
      if (bulkInFlight) {
        pendingRender = true;
        return;
      }
      var failures = results.filter(function(r) { return r.failed; }).length;
      if (failures === results.length) {
        container.innerHTML = errorHtml('drawer');
        return;
      }
      var html = results.map(function(result) {
        if (result.failed) {
          return '';
        }
        return itemHtml(result.product, result.entry);
      }).join('');

      if (list.length > DRAWER_LIMIT) {
        html += '<a href="' + escapeHtml(wishlistUrl) + '" class="wishlist-drawer__view-all">' +
          escapeHtml(t('view_all', 'Visa alla [count] sparade produkter', { count: list.length })) + ' →</a>';
      }
      container.innerHTML = html;
      updateBulkActions();
    });
  }

  /*============================================================
    Page rendering (batched, with "show more")
  ============================================================*/

  function renderPage(reset) {
    if (bulkInFlight) {
      pendingRender = true;
      return;
    }
    var grid = document.querySelector('[data-wishlist-grid]');
    if (!grid) {
      return;
    }
    var footer = document.querySelector('[data-wishlist-page-footer]');
    var list = sortedList();
    log('renderPage', list.length + ' items, reset=' + !!reset);
    updateBulkActions();

    if (reset !== false) {
      pageRenderedCount = 0;
      grid.innerHTML = '';
    }

    if (list.length === 0) {
      grid.innerHTML = emptyHtml('page');
      syncAllButtons(grid);
      if (footer) {
        footer.innerHTML = '';
      }
      return;
    }

    var batch = list.slice(pageRenderedCount, pageRenderedCount + PAGE_BATCH);
    var skeleton = document.createElement('div');
    skeleton.className = 'wishlist-page__skeletons';
    skeleton.innerHTML = skeletonHtml(Math.min(batch.length, 4));
    grid.appendChild(skeleton);

    fetchEntries(batch).then(function(results) {
      if (bulkInFlight) {
        pendingRender = true;
        return;
      }
      skeleton.remove();
      var failures = results.filter(function(r) { return r.failed; }).length;
      if (failures === results.length && pageRenderedCount === 0) {
        grid.innerHTML = errorHtml('page');
        if (footer) {
          footer.innerHTML = '';
        }
        return;
      }
      var html = results.map(function(result) {
        if (result.failed) {
          return '';
        }
        return gridItemHtml(result.product, result.entry);
      }).join('');
      grid.insertAdjacentHTML('beforeend', html);
      pageRenderedCount += batch.length;

      if (footer) {
        footer.innerHTML = pageRenderedCount < list.length
          ? '<button type="button" class="btn" data-wishlist-show-more>' + escapeHtml(t('show_more', 'Visa fler')) + '</button>'
          : '';
      }
      updateBulkActions();
    });
  }

  /*============================================================
    Drawer plumbing
  ============================================================*/

  function isDrawerOpen() {
    var drawer = document.getElementById('WishlistDrawer');
    return !!(drawer && drawer.classList.contains('drawer--is-open'));
  }

  function isOnWishlistPage() {
    return !!document.querySelector('[data-wishlist-grid]');
  }

  function registerDrawer() {
    if (theme.WishlistDrawer) {
      return;
    }
    if (theme.Drawers && document.getElementById('WishlistDrawer')) {
      theme.WishlistDrawer = new theme.Drawers('WishlistDrawer', 'wishlist');
      log('drawer registered via theme.Drawers');
    } else {
      log('theme.Drawers unavailable, using class-toggle fallback');
    }
  }

  function closeWishlistDrawer() {
    if (theme.WishlistDrawer && theme.WishlistDrawer.close) {
      theme.WishlistDrawer.close();
    } else {
      fallbackToggleDrawer(false);
    }
  }

  // Fallback used only when theme.Drawers is missing. When theme.Drawers is
  // active its own click handler stops propagation, so this never fires twice.
  function fallbackToggleDrawer(open) {
    var drawer = document.getElementById('WishlistDrawer');
    if (!drawer) {
      return;
    }
    drawer.classList.toggle('drawer--is-open', open);
  }

  /*============================================================
    Delegated click handling
  ============================================================*/

  document.addEventListener('click', function(evt) {
    var addAllBtn = evt.target.closest('[data-wishlist-add-all]');
    if (addAllBtn) {
      evt.preventDefault();
      var ctx = isOnWishlistPage() && !isDrawerOpen() ? 'page' : 'drawer';
      addAllToCart(ctx);
      return;
    }

    var clearBtn = evt.target.closest('[data-wishlist-clear]');
    if (clearBtn) {
      evt.preventDefault();
      clear();
      return;
    }

    var toggleBtn = evt.target.closest('[data-wishlist-button]');
    if (toggleBtn) {
      if (bulkInFlight) {
        evt.preventDefault();
        return;
      }
      evt.preventDefault();
      evt.stopPropagation();
      handleToggleClick(toggleBtn);
      return;
    }

    var atcBtn = evt.target.closest('[data-wishlist-atc]');
    if (atcBtn) {
      if (bulkInFlight) {
        evt.preventDefault();
        return;
      }
      evt.preventDefault();
      handleAtcClick(atcBtn);
      return;
    }

    var removeBtn = evt.target.closest('[data-wishlist-remove]');
    if (removeBtn) {
      if (bulkInFlight) {
        evt.preventDefault();
        return;
      }
      evt.preventDefault();
      handleRemoveClick(removeBtn);
      return;
    }

    var variantSelect = evt.target.closest('[data-wishlist-variant-select]');
    if (variantSelect && bulkInFlight) {
      evt.preventDefault();
      return;
    }

    var retryBtn = evt.target.closest('[data-wishlist-retry]');
    if (retryBtn) {
      if (retryBtn.getAttribute('data-wishlist-context') === 'page') {
        renderPage(true);
      } else {
        renderDrawer();
      }
      return;
    }

    var showMoreBtn = evt.target.closest('[data-wishlist-show-more]');
    if (showMoreBtn) {
      renderPage(false);
      return;
    }

    // Fallback open/close when theme.Drawers is not available. With
    // theme.Drawers active, its handlers stopPropagation so we never get here.
    if (!theme.WishlistDrawer) {
      var openBtn = evt.target.closest('.js-drawer-open-wishlist');
      if (openBtn) {
        evt.preventDefault();
        fallbackToggleDrawer(true);
        renderDrawer();
        return;
      }
      var drawerEl = document.getElementById('WishlistDrawer');
      if (drawerEl && drawerEl.classList.contains('drawer--is-open') && evt.target.closest('.js-drawer-close') && drawerEl.contains(evt.target)) {
        fallbackToggleDrawer(false);
      }
    }
  });

  // Prevent variant select changes while locked (change event, not click).
  document.addEventListener('change', function(evt) {
    if (bulkInFlight && evt.target && evt.target.matches && evt.target.matches('[data-wishlist-variant-select]')) {
      evt.preventDefault();
      evt.stopPropagation();
    }
  }, true);

  function handleToggleClick(btn) {
    if (bulkInFlight) {
      return;
    }
    var handle = btn.getAttribute('data-product-handle');
    if (!handle) {
      return;
    }
    var variantId = null;
    var form = btn.closest('form');
    var variantInput = form && form.querySelector('[name="id"]');
    if (variantInput && variantInput.value) {
      variantId = parseInt(variantInput.value, 10) || null;
    } else if (btn.getAttribute('data-variant-id')) {
      variantId = parseInt(btn.getAttribute('data-variant-id'), 10) || null;
    }
    // Cards save without variant context.
    if (btn.classList.contains('wishlist-button--card')) {
      variantId = null;
    }

    var source = btn.getAttribute('data-wishlist-source');
    if (!source) {
      if (btn.classList.contains('wishlist-button--card')) {
        source = 'card';
      } else if (btn.classList.contains('wishlist-button--product')) {
        source = 'pdp';
      } else {
        source = 'api';
      }
    }

    // Immediate visual feedback on the clicked button, before storage/events.
    var willBeActive = !btn.classList.contains('is-active');
    setButtonState(btn, willBeActive);
    if (willBeActive) {
      btn.classList.add('wishlist-button--bounce');
      setTimeout(function() {
        btn.classList.remove('wishlist-button--bounce');
      }, 400);
    }

    var nowSaved = toggle({ handle: handle, variantId: variantId }, source);
    if (nowSaved) {
      showToast(t('saved_toast', 'Sparad i önskelistan'));
    }
  }

  // Soft removal: fade the card out before mutating (re-render follows).
  function handleRemoveClick(btn) {
    if (bulkInFlight) {
      return;
    }
    var handle = btn.getAttribute('data-product-handle');
    var item = btn.closest('[data-wishlist-item]');
    if (item) {
      item.classList.add('wishlist-item--removing');
      setTimeout(function() {
        remove(handle);
      }, 200);
    } else {
      remove(handle);
    }
  }

  /*============================================================
    Reactive listeners
  ============================================================*/

  document.addEventListener('wishlist:updated', function(evt) {
    if (bulkInFlight) {
      pendingRender = true;
      // Still sync badge count from live list for header accuracy.
      syncBadge(evt.detail.count);
      if (evt.detail.item && evt.detail.item.handle) {
        var saved = evt.detail.list.some(function(item) {
          return item.handle === evt.detail.item.handle;
        });
        syncButtonsForHandle(evt.detail.item.handle, saved);
      }
      return;
    }

    syncBadge(evt.detail.count);
    if (evt.detail.item && evt.detail.item.handle) {
      var isSaved = evt.detail.list.some(function(item) {
        return item.handle === evt.detail.item.handle;
      });
      syncButtonsForHandle(evt.detail.item.handle, isSaved);
    } else {
      syncAllButtons();
    }
    updateBulkActions();
    requestRender();
  });

  // Drawer content renders when theme.Drawers announces open.
  document.addEventListener('drawerOpen.WishlistDrawer', function() {
    renderDrawer();
    try {
      pushDataLayer({
        event: 'wishlist_drawer_open',
        wishlist_count: readList().length
      });
    } catch (err) {
      // ignore
    }
  });

  // Cross-tab sync. Note: "storage" never fires in the tab that wrote —
  // same-tab UI is handled by the custom events above. The receiving tab
  // only adopts the new state; it never pushes (writing tab owns the push).
  window.addEventListener('storage', function(evt) {
    if (evt.key === META_KEY) {
      sync.state.baseRev = (function() {
        try {
          var parsed = JSON.parse(evt.newValue || '{}');
          return typeof parsed.baseRev === 'number' ? parsed.baseRev : sync.state.baseRev;
        } catch (err) {
          return sync.state.baseRev;
        }
      })();
      return;
    }
    if (evt.key !== STORAGE_KEY) {
      return;
    }
    if (clearPending) {
      log('cross-tab storage ignored: clearPending');
      return;
    }
    log('cross-tab storage event');
    memoryList = null;
    productCache.clear();
    if (bulkInFlight) {
      try {
        pendingAdoptList = sanitizeList(JSON.parse(evt.newValue || '[]')) || [];
      } catch (err) {
        pendingAdoptList = [];
      }
      pendingRender = true;
      return;
    }
    syncBadge();
    syncAllButtons();
    updateBulkActions();
    if (isDrawerOpen()) {
      renderDrawer();
    }
    if (isOnWishlistPage()) {
      renderPage(true);
    }
  });

  // Theme editor / dynamic sections: clicks are covered by delegation,
  // this only syncs the visual state of freshly rendered buttons.
  document.addEventListener('shopify:section:load', function(evt) {
    syncAllButtons(evt.target);
    updateBulkActions();
  });

  // pagehide: route active run to finishing for partial summary (runId dedupe).
  window.addEventListener('pagehide', function() {
    if (bulkInFlight && currentRun && (runState === 'starting' || runState === 'running')) {
      finishRun(true);
    }
  });

  /*============================================================
    Init
  ============================================================*/

  function init() {
    migrate();
    registerDrawer();
    sync.init();
    syncBadge();
    syncAllButtons();
    updateBulkActions();
    if (isOnWishlistPage()) {
      renderPage(true);
    }
    maybeFirePurchase();
    log('initialized,', readList().length, 'items, sync', sync.state.enabled ? 'on' : 'off');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Late checkout object (thank-you) may appear after our script.
  try {
    if (window.Shopify) {
      var checkoutDesc = Object.getOwnPropertyDescriptor(Shopify, 'checkout');
      if (!checkoutDesc || checkoutDesc.configurable) {
        var _checkout = Shopify.checkout;
        Object.defineProperty(Shopify, 'checkout', {
          configurable: true,
          enumerable: true,
          get: function() { return _checkout; },
          set: function(v) {
            _checkout = v;
            maybeFirePurchase();
          }
        });
        if (_checkout) {
          maybeFirePurchase();
        }
      }
    }
  } catch (err) {
    // defineProperty may fail on sealed hosts — init already called maybeFirePurchase
  }

  theme.Wishlist = {
    readList: readList,
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    clear: clear,
    addAllToCart: addAllToCart,
    mergeLists: mergeLists,
    fetchProduct: fetchProduct,
    renderDrawer: renderDrawer,
    renderPage: renderPage,
    showToast: showToast,
    toMoneyMajor: toMoneyMajor,
    sync: sync
  };
})();
